import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useUseCase } from './UseCaseContext';
import { logger } from '../utils/logger';

interface PeriodContextType {
  periods: string[];
  selectedPeriod: string;
  setSelectedPeriod: (period: string) => void;
  loading: boolean;
}

const PeriodContext = createContext<PeriodContextType | undefined>(undefined);

export function PeriodProvider({ children }: { children: ReactNode }) {
  const [periods, setPeriods] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const { useCase } = useUseCase();

  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';

  useEffect(() => {
    setLoading(true);
    setPeriods([]);
    setSelectedPeriod('');

    // Fetch available periods from API
    fetch(API_ENDPOINTS.periods(serviceType))
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to fetch periods: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        const availablePeriods: string[] = data.periods || [];
        // Logistics demo: only periods ending on/after Jan 2026. Emergency Services: show all uploaded periods
        // (e.g. 20240701_to_20240727 from S3) so utilisation maps to real ES pipeline outputs.
        const filtered =
          serviceType === 'emergency_services'
            ? availablePeriods
            : availablePeriods.filter((p: string) => {
                const endPart = (p.match(/_to_(\d{8})$/) || p.match(/\d{8}$/))?.[1] || p.slice(-8);
                return endPart >= '20260101';
              });
        // Sort so most recent period is first (e.g. 20260301_to_20260316 before 20260101_to_20260131)
        const sorted = [...filtered].sort((a, b) => b.localeCompare(a));
        setPeriods(sorted);
        if (sorted.length > 0) {
          setSelectedPeriod(sorted[0]);
        }
        setLoading(false);
      })
      .catch(err => {
        logger.error('Error loading periods:', err);
        // Fallback to hardcoded period if API fails
        const fallbackPeriod = '20260120_to_20260128';
        setPeriods([fallbackPeriod]);
        setSelectedPeriod(fallbackPeriod);
        setLoading(false);
      });
  }, [serviceType]);

  return (
    <PeriodContext.Provider value={{ periods, selectedPeriod, setSelectedPeriod, loading }}>
      {children}
    </PeriodContext.Provider>
  );
}

export function usePeriod() {
  const context = useContext(PeriodContext);
  if (context === undefined) {
    throw new Error('usePeriod must be used within a PeriodProvider');
  }
  return context;
}

